import {
    MemberId,
    MemberSummary,
    MissionReport,
    MissionReportParticipantInfo,
    MissionReportParticipantPresence,
    MissionSummary,
} from '@wwf/sdk';
import { collectDistinctParticipantIdsFromMissionSessions } from '@domain/mission';
import { MemberLightCard } from '@app/member/show/MemberLightCard';
import { DialogCloseButton } from '@app/misc/DialogCloseButton';
import { LabelValueRow } from '@app/misc/LabelValueRow';
import { useDrawer } from '@app/misc/DrawerContext';
import {
    Typography,
    TextField,
    Button,
    Card,
    CardContent,
    Divider,
    Stack,
    FormControlLabel,
    Switch,
} from '@mui/material';
import { Box } from '@mui/system';
import { format } from 'date-fns';
import { useRefresh, useUpdate } from 'ra-core';
import { useState, useEffect, useMemo, useCallback } from 'react';
import { RecordContextProvider, ReferenceField, useGetMany } from 'react-admin';

const getDefaultReport = (record: MissionSummary): MissionReport => ({
    type: 'field',
    details: '',
    participantInfos: collectDistinctParticipantIdsFromMissionSessions(
        record.sessions
    ).map(participantId => ({
        id: participantId,
        presence: 'Present',
        workHours: null,
    })),
    partnerInfos: null,
});

function getMissionReport(report: MissionSummary['report']): MissionReport {
    return JSON.parse(report ?? '{}') as MissionReport;
}

const formatPresence = (presence: MissionReportParticipantPresence) => {
    switch (presence) {
        case 'Present':
            return 'Present';
        case 'Absent_Warned':
            return 'Absent (warned)';
        case 'Absent_Not_Warned':
            return 'Absent (not warned)';
    }
};

const formatBoolean = (value: boolean | null | undefined) => {
    if (value == null) return '-';
    return value ? 'Yes' : 'No';
};

const parseNullableNumber = (value: string) => {
    if (!value.trim()) return null;
    const parsed = Number(value);
    return Number.isNaN(parsed) ? null : parsed;
};

const MissionReportEditorDrawer = ({
    record,
    onClose,
}: {
    record: MissionSummary;
    onClose: () => void;
}) => {
    const refresh = useRefresh();
    const [update, { isPending }] = useUpdate();
    const [editedReport, setEditedReport] = useState<MissionReport>(
        record.report
            ? getMissionReport(record.report)
            : getDefaultReport(record)
    );

    useEffect(() => {
        setEditedReport(
            record.report
                ? getMissionReport(record.report)
                : getDefaultReport(record)
        );
    }, [record]);

    const participantIds = useMemo(
        () => editedReport.participantInfos.map(participant => participant.id),
        [editedReport.participantInfos]
    );
    const { data: participants } = useGetMany(
        'member',
        { ids: participantIds },
        { enabled: participantIds.length > 0 }
    );
    const participantsById = useMemo(
        () =>
            new Map(
                (participants ?? []).map(participant => [
                    String(participant.id),
                    participant,
                ])
            ),
        [participants]
    );

    const updateParticipant = useCallback(
        (
            id: string,
            updater: (
                current: MissionReportParticipantInfo
            ) => MissionReportParticipantInfo
        ) => {
            setEditedReport(current => ({
                ...current,
                participantInfos: current.participantInfos.map(participant =>
                    participant.id === id ? updater(participant) : participant
                ),
            }));
        },
        []
    );

    const handleSave = useCallback(() => {
        update(
            'mission',
            {
                id: record.id,
                data: { report: editedReport },
                previousData: { report: record.report },
            },
            {
                onSuccess: () => {
                    refresh();
                    onClose();
                },
            }
        );
    }, [editedReport, onClose, record.id, record.report, refresh, update]);

    return (
        <Box sx={{ position: 'relative', height: '100%' }}>
            <DialogCloseButton onClose={onClose} />
            <Box p={3} pt={4}>
                <Typography variant="h6" mb={2}>
                    Edit report
                </Typography>
                <Stack gap={2}>
                    <TextField
                        label="Details"
                        value={editedReport.details}
                        onChange={event =>
                            setEditedReport(current => ({
                                ...current,
                                details: event.target.value,
                            }))
                        }
                        multiline
                        minRows={4}
                        fullWidth
                    />

                    {editedReport.partnerInfos && (
                        <Stack>
                            <Typography
                                variant="subtitle2"
                                color="textSecondary"
                            >
                                Partner info
                            </Typography>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={
                                            editedReport.partnerInfos.wasOk
                                        }
                                        onChange={(_, checked) =>
                                            setEditedReport(current => ({
                                                ...current,
                                                partnerInfos:
                                                    current.partnerInfos
                                                        ? {
                                                              ...current.partnerInfos,
                                                              wasOk: checked,
                                                          }
                                                        : null,
                                            }))
                                        }
                                    />
                                }
                                label="Was ok"
                            />
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={
                                            editedReport.partnerInfos
                                                .sustainable
                                        }
                                        onChange={(_, checked) =>
                                            setEditedReport(current => {
                                                if (!current.partnerInfos) {
                                                    return current;
                                                }
                                                return {
                                                    ...current,
                                                    partnerInfos: {
                                                        ...current.partnerInfos,
                                                        sustainable: checked,
                                                    },
                                                };
                                            })
                                        }
                                    />
                                }
                                label="Sustainable"
                            />
                        </Stack>
                    )}

                    {editedReport.type === 'awareness' && (
                        <TextField
                            label="Sales figure"
                            type="number"
                            value={editedReport.salesFigure ?? ''}
                            onChange={event =>
                                setEditedReport(current => ({
                                    ...current,
                                    salesFigure: parseNullableNumber(
                                        event.target.value
                                    ),
                                }))
                            }
                            fullWidth
                        />
                    )}

                    {editedReport.type === 'school' && (
                        <Stack gap={1}>
                            <TextField
                                label="Awared child count"
                                type="number"
                                value={editedReport.awaredChildCount ?? ''}
                                onChange={event =>
                                    setEditedReport(current => ({
                                        ...current,
                                        awaredChildCount: parseNullableNumber(
                                            event.target.value
                                        ),
                                    }))
                                }
                                fullWidth
                            />
                            <TextField
                                label="Knew about WWF count"
                                type="number"
                                value={editedReport.knewAboutWWFCount ?? ''}
                                onChange={event =>
                                    setEditedReport(current => ({
                                        ...current,
                                        knewAboutWWFCount: parseNullableNumber(
                                            event.target.value
                                        ),
                                    }))
                                }
                                fullWidth
                            />
                            <TextField
                                label="Knew about subject count"
                                type="number"
                                value={editedReport.knewAboutSubjectCount ?? ''}
                                onChange={event =>
                                    setEditedReport(current => ({
                                        ...current,
                                        knewAboutSubjectCount:
                                            parseNullableNumber(
                                                event.target.value
                                            ),
                                    }))
                                }
                                fullWidth
                            />
                        </Stack>
                    )}

                    <Divider />
                    <Typography variant="subtitle2" color="textSecondary">
                        Participants
                    </Typography>
                    {editedReport.participantInfos.map(participant => {
                        const member = participantsById.get(participant.id);
                        const isPresent = participant.presence === 'Present';
                        const absentWarned =
                            participant.presence === 'Absent_Warned';
                        return (
                            <Box
                                key={participant.id}
                                border={1}
                                borderColor="divider"
                                borderRadius={1}
                                p={1.5}
                            >
                                <Stack gap={1}>
                                    {member ? (
                                        <RecordContextProvider value={member}>
                                            <MemberLightCard noLink />
                                        </RecordContextProvider>
                                    ) : (
                                        <Typography variant="body2">
                                            {`Member: ${participant.id}`}
                                        </Typography>
                                    )}

                                    <FormControlLabel
                                        control={
                                            <Switch
                                                checked={isPresent}
                                                onChange={(_, checked) =>
                                                    updateParticipant(
                                                        participant.id,
                                                        current => ({
                                                            ...current,
                                                            presence: checked
                                                                ? 'Present'
                                                                : 'Absent_Not_Warned',
                                                            workHours: checked
                                                                ? (current.workHours ??
                                                                  1)
                                                                : null,
                                                        })
                                                    )
                                                }
                                            />
                                        }
                                        label="Present"
                                    />

                                    {isPresent ? (
                                        <TextField
                                            label="Work hours"
                                            type="number"
                                            value={participant.workHours ?? ''}
                                            onChange={event =>
                                                updateParticipant(
                                                    participant.id,
                                                    current => ({
                                                        ...current,
                                                        workHours:
                                                            parseNullableNumber(
                                                                event.target
                                                                    .value
                                                            ),
                                                    })
                                                )
                                            }
                                            fullWidth
                                        />
                                    ) : (
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={absentWarned}
                                                    onChange={(_, checked) =>
                                                        updateParticipant(
                                                            participant.id,
                                                            current => ({
                                                                ...current,
                                                                presence:
                                                                    checked
                                                                        ? 'Absent_Warned'
                                                                        : 'Absent_Not_Warned',
                                                            })
                                                        )
                                                    }
                                                />
                                            }
                                            label="Absent warned"
                                        />
                                    )}
                                </Stack>
                            </Box>
                        );
                    })}

                    <Box mt={1}>
                        <Button
                            variant="contained"
                            onClick={handleSave}
                            disabled={isPending}
                        >
                            Save report
                        </Button>
                    </Box>
                </Stack>
            </Box>
        </Box>
    );
};

export const MissionReportTab = ({ record }: { record: MissionSummary }) => {
    const drawer = useDrawer();
    const missionReport = useMemo(
        () =>
            record.report
                ? getMissionReport(record.report)
                : getDefaultReport(record),
        [record]
    );
    const participantIds = useMemo(
        () =>
            missionReport.participantInfos.map(
                participant => participant.id as MemberId
            ),
        [missionReport.participantInfos]
    );
    const { data: participants } = useGetMany<MemberSummary>(
        'member',
        { ids: participantIds },
        { enabled: participantIds.length > 0 }
    );

    const participantsById = useMemo(
        () =>
            new Map(
                (participants ?? []).map(participant => [
                    participant.id,
                    participant,
                ])
            ),
        [participants]
    );

    const openEditor = useCallback(() => {
        drawer.openDrawer(state => (
            <MissionReportEditorDrawer
                record={record}
                onClose={state.onClose}
            />
        ));
    }, [drawer, record]);

    return (
        <Box display="flex" mb={2} flexDirection="column" gap={2}>
            <Card variant="outlined">
                <CardContent>
                    <Stack gap={1}>
                        <LabelValueRow
                            label="Report sent at"
                            value={
                                record.reportSentDate
                                    ? format(
                                          new Date(record.reportSentDate),
                                          'PPpp'
                                      )
                                    : 'Not officially submitted yet'
                            }
                        />
                        <LabelValueRow
                            label="Officially submitted"
                            value={record.reportSentDate ? 'Yes' : 'No'}
                        />
                    </Stack>
                </CardContent>
            </Card>

            <Card variant="outlined">
                <CardContent>
                    <Stack gap={1.5}>
                        <Typography color="textSecondary" variant="caption">
                            Report content
                        </Typography>
                        <LabelValueRow
                            label="Details"
                            value={missionReport.details || '-'}
                            alignItems="flex-start"
                        />
                        <LabelValueRow
                            label="Partner was ok"
                            value={formatBoolean(
                                missionReport.partnerInfos?.wasOk
                            )}
                        />
                        <LabelValueRow
                            label="Partner sustainable"
                            value={formatBoolean(
                                missionReport.partnerInfos?.sustainable
                            )}
                        />
                        <Divider />
                        <Typography color="textSecondary" variant="caption">
                            Participants
                        </Typography>
                        {missionReport.participantInfos.length === 0 ? (
                            <Typography color="textSecondary" variant="body2">
                                No participants in report
                            </Typography>
                        ) : (
                            <Stack gap={1}>
                                {missionReport.participantInfos.map(
                                    participant => {
                                        const memberSummary =
                                            participantsById.get(
                                                participant.id
                                            ) ?? undefined;

                                        console.log(memberSummary);
                                        return (
                                            <Box
                                                key={participant.id}
                                                border={1}
                                                borderColor="divider"
                                                borderRadius={1}
                                                p={1.5}
                                            >
                                                <Stack gap={0.75}>
                                                    {memberSummary ? (
                                                        <ReferenceField
                                                            source="id"
                                                            reference="member"
                                                            key={participant.id}
                                                            record={
                                                                memberSummary
                                                            }
                                                        >
                                                            <MemberLightCard />
                                                        </ReferenceField>
                                                    ) : (
                                                        <Typography variant="body2">
                                                            {`Member: ${participant.id}`}
                                                        </Typography>
                                                    )}
                                                    <LabelValueRow
                                                        label="Presence"
                                                        value={formatPresence(
                                                            participant.presence
                                                        )}
                                                    />
                                                    <LabelValueRow
                                                        label="Work hours"
                                                        value={
                                                            participant.workHours ??
                                                            '-'
                                                        }
                                                    />
                                                </Stack>
                                            </Box>
                                        );
                                    }
                                )}
                            </Stack>
                        )}
                    </Stack>
                </CardContent>
            </Card>

            <Box>
                <Button variant="outlined" onClick={openEditor}>
                    Edit report
                </Button>
            </Box>
        </Box>
    );
};
